/**
 * Amplitude + Klaviyo Event Tracking for Nawa Shopify Theme
 * Tracks: product_wishlisted, product_unwishlisted, notify_me_clicked,
 *         Size Chart Opened, Search Performed
 */

(function() {
  'use strict';

  // Klaviyo: ensure the queue exists so pushes work even before klaviyo.js (async) loads.
  window._learnq = window._learnq || [];

  // Reuse the same anonymous id key as snippets/main-product__info-blocks.liquid
  // so wishlist + size-chart + search events tie to one anonymous Klaviyo profile.
  function getKlaviyoAnonymousId() {
    let id = localStorage.getItem('klaviyo_anonymous_id');
    if (!id) {
      id = 'anon_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('klaviyo_anonymous_id', id);
    }
    return id;
  }

  // Klaviyo silently drops track events with no identified profile.
  // Always identify (email when logged in, otherwise $anonymous) before tracking.
  // window.__nawaCustomerEmail is emitted from layout/theme.liquid for logged-in customers.
  function klaviyoTrack(eventName, props) {
    const customerEmail = window.__nawaCustomerEmail || null;
    const eventProps = Object.assign({}, props);
    console.log(customerEmail);
    if (customerEmail) {
      eventProps['$email'] = customerEmail;
      window._learnq.push(['identify', { '$email': customerEmail }]);
    } else {
      const anonId = getKlaviyoAnonymousId();
      eventProps['$anonymous'] = anonId;
      window._learnq.push(['identify', { '$anonymous': anonId }]);
    }

    window._learnq.push(['track', eventName, eventProps]);
  }

  // Deduplication cache to prevent duplicate events
  const eventCache = {
    data: new Map(),
    add: function(eventName, identifier) {
      const key = `${eventName}_${identifier}`;
      const now = Date.now();
      const lastTracked = this.data.get(key);

      // If this event was tracked in the last 2 seconds, skip it
      if (lastTracked && (now - lastTracked) < 2000) {
        return false; // Duplicate detected
      }

      this.data.set(key, now);
      return true; // Safe to track
    }
  };

  // Wait for Amplitude to be ready
  function waitForAmplitude(callback) {
    if (window.amplitude && typeof window.amplitude.track === 'function') {
      callback();
    } else {
      setTimeout(() => waitForAmplitude(callback), 100);
    }
  }

  // Track Wishlist Events
  function initWishlistTracking() {
    let swymNativeEventsAvailable = false;

    // Method 1: Use Swym's native event system (preferred)
    function setupSwymEventListeners() {
      // Wait for Swym to be ready
      function onSwymReady(callback) {
        if (window.SwymCallbacks) {
          window.SwymCallbacks.push(callback);
        } else {
          window.SwymCallbacks = [callback];
        }
      }

      onSwymReady(function() {
        // Listen for Swym's native wishlist events
        if (window._swat && window._swat.evtLayer && window._swat.JSEvents) {
          swymNativeEventsAvailable = true;

          // Listen for add to wishlist events
          window._swat.evtLayer.addEventListener(window._swat.JSEvents.addedToWishlist, function(event) {
            const product = event.d || {};
            const identifier = `${product.empi || product.epi}_${product.du || product.epi}`;

            // Check for duplicates
            if (!eventCache.add('product_wishlisted', identifier)) {
              console.log('[Amplitude] Duplicate product_wishlisted event blocked');
              return;
            }

            amplitude.track('product_wishlisted', {
              product_id: product.empi || product.epi,
              variant_id: product.du || product.epi,
              product_url: product.du,
              product_name: product.dt,
              currency: product.cu,
              price: product.pr,
              timestamp: new Date().toISOString()
            });
            console.log('[Amplitude] product_wishlisted tracked:', { product_id: product.empi || product.epi });
          });

          // Listen for remove from wishlist events
          window._swat.evtLayer.addEventListener(window._swat.JSEvents.removedFromWishlist, function(event) {
            const product = event.d || {};
            const identifier = `${product.empi || product.epi}_${product.du || product.epi}`;

            // Check for duplicates
            if (!eventCache.add('product_unwishlisted', identifier)) {
              console.log('[Amplitude] Duplicate product_unwishlisted event blocked');
              return;
            }

            amplitude.track('product_unwishlisted', {
              product_id: product.empi || product.epi,
              variant_id: product.du || product.epi,
              product_url: product.du,
              product_name: product.dt,
              timestamp: new Date().toISOString()
            });
            console.log('[Amplitude] product_unwishlisted tracked:', { product_id: product.empi || product.epi });
          });

          console.log('[Amplitude] Swym native event listeners registered successfully');
        } else {
          console.log('[Amplitude] Swym native events not available, will use button observer fallback');
        }
      });
    }

    // Initialize Swym event listeners
    setupSwymEventListeners();

    // Only use button observer as fallback if Swym events are not available
    // We'll check after a delay to give Swym time to load
    setTimeout(() => {
      if (!swymNativeEventsAvailable) {
        console.log('[Amplitude] Activating button observer fallback for wishlist tracking');
        initButtonObserverFallback();
      }
    }, 3000);
  }

  // Fallback method: Button observer (only used if Swym native events aren't available)
  function initButtonObserverFallback() {
    const observedButtons = new WeakSet();

    const observeWishlistButton = (button) => {
      // Prevent observing the same button multiple times
      if (observedButtons.has(button)) {
        return;
      }
      observedButtons.add(button);

      let wasAdded = button.classList.contains('swym-added');

      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            const isAdded = button.classList.contains('swym-added');

            // Check if state changed
            if (isAdded !== wasAdded) {
              const productId = button.getAttribute('data-product-id');
              const variantId = button.getAttribute('data-variant-id');
              const productUrl = button.getAttribute('data-product-url');
              const identifier = `${productId}_${variantId}`;

              if (isAdded && !wasAdded) {
                // Check for duplicates
                if (!eventCache.add('product_wishlisted', identifier)) {
                  console.log('[Amplitude] Duplicate product_wishlisted event blocked (button observer)');
                  wasAdded = isAdded;
                  return;
                }

                // Product was added to wishlist
                amplitude.track('product_wishlisted', {
                  product_id: productId,
                  variant_id: variantId,
                  product_url: productUrl,
                  timestamp: new Date().toISOString()
                });
                console.log('[Amplitude] product_wishlisted tracked (fallback):', { product_id: productId });
              } else if (!isAdded && wasAdded) {
                // Check for duplicates
                if (!eventCache.add('product_unwishlisted', identifier)) {
                  console.log('[Amplitude] Duplicate product_unwishlisted event blocked (button observer)');
                  wasAdded = isAdded;
                  return;
                }

                // Product was removed from wishlist
                amplitude.track('product_unwishlisted', {
                  product_id: productId,
                  variant_id: variantId,
                  product_url: productUrl,
                  timestamp: new Date().toISOString()
                });
                console.log('[Amplitude] product_unwishlisted tracked (fallback):', { product_id: productId });
              }

              wasAdded = isAdded;
            }
          }
        });
      });

      observer.observe(button, {
        attributes: true,
        attributeFilter: ['class']
      });
    };

    // Observe all wishlist buttons
    const wishlistButtons = document.querySelectorAll('.swym-add-to-wishlist, .swym-button[data-swaction="addToWishlist"]');
    wishlistButtons.forEach(button => {
      observeWishlistButton(button);
    });

    // Watch for dynamically added wishlist buttons
    const bodyObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            // Check if the added node is a wishlist button
            if (node.matches && node.matches('.swym-add-to-wishlist, .swym-button[data-swaction="addToWishlist"]')) {
              observeWishlistButton(node);
            }
            // Check for wishlist buttons within the added node
            const buttons = node.querySelectorAll && node.querySelectorAll('.swym-add-to-wishlist, .swym-button[data-swaction="addToWishlist"]');
            if (buttons) {
              buttons.forEach(button => observeWishlistButton(button));
            }
          }
        });
      });
    });

    bodyObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Track Notify Me Events
  function initNotifyMeTracking() {
    // Track when notify me overlay is shown and email is submitted
    document.addEventListener('click', function(e) {
      // Check for mobile and desktop notify me submit buttons
      const submitBtn = e.target.closest('.mobile-out-of-stock-overlay__submit-btn, .desktop-out-of-stock-overlay__submit-btn');

      if (submitBtn) {
        // Get the overlay type
        const isMobile = submitBtn.classList.contains('mobile-out-of-stock-overlay__submit-btn');
        const overlayType = isMobile ? 'mobile' : 'desktop';

        // Find the email input in the same overlay
        const overlay = submitBtn.closest(
          isMobile ? '.mobile-out-of-stock-overlay' : '.desktop-out-of-stock-overlay'
        );

        if (overlay) {
          const emailInput = overlay.querySelector('input[type="email"]');
          const email = emailInput ? emailInput.value.trim() : '';

          // Validate email before tracking
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (email && emailRegex.test(email)) {
            // Get product data from data attributes
            const productId = emailInput.getAttribute('data-product-id');
            const variantId = emailInput.getAttribute('data-variant-id');
            const productName = emailInput.getAttribute('data-product-name');
            const identifier = `${productId}_${variantId}_${email}`;

            // Check for duplicates
            if (!eventCache.add('notify_me_clicked', identifier)) {
              console.log('[Amplitude] Duplicate notify_me_clicked event blocked');
              return;
            }

            // amplitude.track('notify_me_clicked', {
            //   product_id: productId,
            //   variant_id: variantId,
            //   product_name: productName,
            //   email: email,
            //   overlay_type: overlayType,
            //   timestamp: new Date().toISOString()
            // });

            // console.log('[Amplitude] notify_me_clicked tracked:', {
            //   product_id: productId,
            //   variant_id: variantId,
            //   overlay_type: overlayType
            // });
          }
        }
      }
    }, true); // Use capture phase to catch event early
  }

  // Track Newsletter Subscription Events
  function initNewsletterTracking() {
    // Expose a global function for the Klaviyo newsletter form to call
    window.trackNewsletterSubscription = function(email, listId, source, locale) {
      // Ensure Amplitude is ready
      waitForAmplitude(() => {
        const identifier = email;

        // Check for duplicates
        if (!eventCache.add('newsletter_subscribed', identifier)) {
          console.log('[Amplitude] Duplicate newsletter_subscribed event blocked');
          return;
        }

        amplitude.track('newsletter_subscribed', {
          email: email,
          list_id: listId || 'WA6D8A',
          source: source || 'Newsletter Footer Form',
          locale: locale || 'en',
          timestamp: new Date().toISOString()
        });

        console.log('[Amplitude] newsletter_subscribed tracked:', { email: email });
      });
    };

    console.log('[Amplitude] Newsletter tracking initialized');
  }

  // Track Add to Cart Events
  function initAddToCartTracking() {
    // Store the original fetch function
    const originalFetch = window.fetch;

    // Override the fetch function to intercept cart add requests
    window.fetch = function(...args) {
      const [url, config] = args;

      // Check if this is a cart add request
      const isCartAddRequest =
        (typeof url === 'string' && (url.includes('/cart/add.js') || url.includes('/cart/add'))) ||
        (url instanceof URL && url.pathname.includes('/cart/add'));

      if (isCartAddRequest && config?.method === 'POST') {
        // Call the original fetch
        return originalFetch.apply(this, args).then(async (response) => {
          // Clone the response so we can read it without consuming it
          const clonedResponse = response.clone();

          try {
            // Parse the response to get cart item details
            const data = await clonedResponse.json();

            // Ensure Amplitude is ready before tracking
            waitForAmplitude(() => {
              // Check if the response is successful (has item data)
              if (data && !data.status && (data.id || data.variant_id)) {
                const variantId = data.variant_id || data.id;
                const quantity = data.quantity || 1;
                const identifier = `${variantId}_${quantity}`;

                // Check for duplicates (within 2 seconds)
                if (!eventCache.add('product_added_to_cart', identifier)) {
                  console.log('[Amplitude] Duplicate product_added_to_cart event blocked');
                  return response;
                }

                fetch('/cart.js')
                 .then(r => r.json())
                 .then(cart => {  
                      const checkoutUrl = `https://nawa.world/cart`;

                     // Klaviyo — Added to Cart with CheckoutURL
                        klaviyoTrack('Added to Cart Custom', {
                            CheckoutURL: checkoutUrl,
                            product_id: data.product_id || data.id,
                            variant_id: data.variant_id || data.id,
                            quantity: data.quantity || 1,
                            price: data.price ? (data.price / 100) : data.final_price ? (data.final_price / 100) : 0,
                            product_title: data.product_title || data.title || '',
                            variant_title: data.variant_title || '',
                            cart_value: cart.total_price / 100,
                            items: cart.items.map(item => ({
                            product_id: item.product_id,
                            variant_id: item.variant_id,
                            title: item.product_title,
                            variant_title: item.variant_title,
                            quantity: item.quantity,
                            price: item.price / 100,
                            sku: item.sku
                   })),
                 timestamp: new Date().toISOString()
                      });

                 console.log('[Klaviyo] Added to Cart tracked with CheckoutURL:', checkoutUrl);
                })
                 .catch(err => console.log('[Klaviyo] Could not fetch cart for CheckoutURL:', err));

                  
                

                // Track the add to cart event
                // amplitude.track('product_added_to_cart', {
                //   product_id: data.product_id || data.id,
                //   variant_id: data.variant_id || data.id,
                //   quantity: data.quantity || 1,
                //   price: data.price ? (data.price / 100) : data.final_price ? (data.final_price / 100) : 0,
                //   product_title: data.product_title || data.title || '',
                //   variant_title: data.variant_title || '',
                //   timestamp: new Date().toISOString()
                // });
                //
                // console.log('[Amplitude] product_added_to_cart tracked:', {
                //   product_id: data.product_id || data.id,
                //   variant_id: data.variant_id || data.id,
                //   quantity: data.quantity || 1
                // });
              }
            });
          } catch (error) {
            // If JSON parsing fails, just log and continue
            console.log('[Amplitude] Could not parse cart add response:', error);
          }

          // Return the original response
          return response;
        });
      }

      // For non-cart-add requests, just call the original fetch
      return originalFetch.apply(this, args);
    };

    console.log('[Amplitude] Add to cart tracking initialized');
  }

  // Track Size Chart Opens (Amplitude keeps original name; Klaviyo gets new 'Size Chart Opened')
  function initSizeGuideTracking() {
    function getProductIdFromTrigger(triggerEl) {
      const container = triggerEl && triggerEl.closest('[data-product-id]');
      return container ? container.getAttribute('data-product-id') : null;
    }

    function trackSizeChartOpened(source, productId) {
      const identifier = productId ? `size_chart_${productId}_${Date.now()}` : `size_chart_${Date.now()}`;

      // Check for duplicates
      if (!eventCache.add('size_chart_opened', identifier)) {
        console.log('[Amplitude/Klaviyo] Duplicate size_chart_opened event blocked');
        return;
      }

      const props = {
        source: source,
        timestamp: new Date().toISOString()
      };
      if (productId) props.product_id = productId;

      // Amplitude — original event (already existed, not introducing a new one)
      amplitude.track('size_chart_opened', props);
      console.log('[Amplitude] size_chart_opened tracked:', source, productId || '(no product_id)');

      // Klaviyo — identify-then-track (mirrors snippets/main-product__info-blocks.liquid)
      klaviyoTrack('Size Chart Opened', props);
      console.log('[Klaviyo] Size Chart Opened tracked:', source, productId || '(no product_id)');
    }

    // Track clicks on size guide elements using event delegation
    document.addEventListener('click', function(e) {
      // Mobile size guide button (.size-guide-mobile)
      const mobileTrigger = e.target.closest('.size-guide-mobile');
      if (mobileTrigger) {
        trackSizeChartOpened('mobile', getProductIdFromTrigger(mobileTrigger));
        return;
      }

      // Desktop size guide drawer button (.open-image-drawer)
      const drawerTrigger = e.target.closest('.open-image-drawer');
      if (drawerTrigger) {
        trackSizeChartOpened('desktop_drawer', getProductIdFromTrigger(drawerTrigger));
        return;
      }

      // Desktop size guide details/summary element
      const summaryElement = e.target.closest('summary');
      if (summaryElement) {
        const detailsElement = summaryElement.closest('details');
        if (detailsElement && detailsElement.id === 'Size-Guide-drawer') {
          trackSizeChartOpened('desktop_details', getProductIdFromTrigger(detailsElement));
          return;
        }
      }
    }, true); // Use capture phase to catch events early

    console.log('[Amplitude/Klaviyo] Size guide tracking initialized');
  }

  // Track Search Performed (Amplitude + Klaviyo)
  function initSearchTracking() {
    const DEBOUNCE_MS = 800;
    let debounceTimer = null;

    function attachSearchListener(input) {
      // Prevent attaching duplicate listeners
      if (input._searchTracked) return;
      input._searchTracked = true;

      input.addEventListener('input', function() {
        clearTimeout(debounceTimer);
        const query = input.value.trim();
        if (!query) return;

        debounceTimer = setTimeout(function() {
          if (!eventCache.add('Search Performed', query)) {
            console.log('[Amplitude/Klaviyo] Duplicate Search Performed event blocked');
            return;
          }

          const props = {
            search_query: query,
            timestamp: new Date().toISOString()
          };

          // Amplitude
          amplitude.track('Search Performed', props);
          console.log('[Amplitude] Search Performed tracked:', query);

          // Klaviyo — identify-then-track (mirrors snippets/main-product__info-blocks.liquid)
          klaviyoTrack('Search Performed', props);
          console.log('[Klaviyo] Search Performed tracked:', query);
        }, DEBOUNCE_MS);
      });
    }

    // Attach to any already-present search inputs
    document.querySelectorAll('predictive-search input[name="q"], input[name="q"]').forEach(attachSearchListener);

    // Watch for dynamically rendered search inputs (drawer opens)
    const searchObserver = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('input[name="q"]')) {
            attachSearchListener(node);
          }
          const inputs = node.querySelectorAll && node.querySelectorAll('input[name="q"]');
          if (inputs) inputs.forEach(attachSearchListener);
        });
      });
    });
    searchObserver.observe(document.body, { childList: true, subtree: true });

    console.log('[Amplitude/Klaviyo] Search tracking initialized');
  }

  // Initialize tracking when DOM is ready and Amplitude is loaded
  function initTracking() {
    waitForAmplitude(() => {
      console.log('[Amplitude/Klaviyo] Tracking initialized with API key: ee3817045aebe9cef3200007ea1a5331');

      initWishlistTracking();
      initNotifyMeTracking();
      initNewsletterTracking();
      initAddToCartTracking();
      initSizeGuideTracking();
      initSearchTracking();

      console.log('[Amplitude/Klaviyo] Event tracking setup complete');
    });
  }

  // Start initialization
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTracking);
  } else {
    initTracking();
  }
})();
